
import { Vue, Options, prop } from "vue-class-component";
import { HandoverController } from "@/app/ui/controllers/HandoverController";
import { AccountController } from "@/app/ui/controllers/AccountController";
import { convertDecimalWithComma } from "@/app/infrastructures/misc/Utils";
import { Pagination } from "@/domain/entities/Pagination";
import {
  HandoverFailedStt,
  HandoverFailedSttItem
} from "@/domain/entities/Handover";

class Props {
  handoverId = prop<number>({
    default: 0,
    type: Number
  });
}

@Options({
  name: "EligibilityFailedModal",
  emits: ["close"]
})
class EligibilityFailedModal extends Vue.with(Props) {
  pagination = new Pagination(1, 10);

  mounted() {
    this.init();
  }

  async init() {
    // Modal data comes solely from failed-stt (payload is complete — no
    // getDetailHandover join).
    await HandoverController.getHandoverFailedStt(this.handoverId).catch(() => {
      this.close();
    });
  }

  close() {
    this.$emit("close");
  }

  get isLoading() {
    return HandoverController.isLoadingHandoverFailedStt;
  }

  get handoverFailedStt(): HandoverFailedStt {
    return (
      HandoverController.handoverFailedSttByHandoverId[this.handoverId] ||
      new HandoverFailedStt()
    );
  }

  get isForeign() {
    return AccountController.accountData.accountIsForeign;
  }

  get failedSttList() {
    return this.handoverFailedStt.failedStt.map(
      (item: HandoverFailedSttItem) => {
        const reason = this.isForeign ? item.reasonEn : item.reason;
        return {
          sttNo: item.sttNo,
          shipmentId: item.shipmentId,
          product: item.productType || "—",
          commodity: item.commodityName || "—",
          pieces: item.totalPiece ?? "—",
          grossWeight: `${convertDecimalWithComma(item.grossWeight, 2)} kg`,
          volumeWeight: `${convertDecimalWithComma(item.volumeWeight, 2)} kg`,
          reason: reason || "—"
        };
      }
    );
  }

  get title() {
    const n = this.handoverFailedStt.totalFailed || this.failedSttList.length;
    return this.$t("handover.list.eligibilityModal.title", { n });
  }

  get columns() {
    return [
      {
        name: this.$t("handover.list.eligibilityModal.sttNo"),
        styleHead: "w-50 text-left whitespace-no-wrap",
        render: (item: any) => {
          const shipment = item.shipmentId
            ? `<div class="text-gray-lp-500">${item.shipmentId}</div>`
            : "";
          return `<div class="flex flex-col items-start text-black-lp-300">
              <div class="overflow-ellipsis text-left">${item.sttNo}</div>
              ${shipment}
            </div>`;
        }
      },
      {
        name: this.$t("handover.list.eligibilityModal.product"),
        styleHead: "w-28 text-left whitespace-no-wrap",
        render: (item: any) =>
          `<div class="flex"><div class="rounded px-2 py-0.5 bg-gray-lp-200 text-black-lp-300">${item.product}</div></div>`
      },
      {
        name: this.$t("handover.list.eligibilityModal.commodity"),
        styleHead: "w-40 text-left whitespace-no-wrap",
        render: (item: any) =>
          `<div class="overflow-ellipsis text-left text-black-lp-300">${item.commodity}</div>`
      },
      {
        name: this.$t("handover.list.eligibilityModal.pieces"),
        styleHead: "w-20 text-left whitespace-no-wrap",
        render: (item: any) =>
          `<div class="overflow-ellipsis text-left text-black-lp-300">${item.pieces}</div>`
      },
      {
        name: this.$t("handover.list.eligibilityModal.grossWeight"),
        styleHead: "w-32 text-left whitespace-no-wrap",
        render: (item: any) =>
          `<div class="overflow-ellipsis text-left text-black-lp-300">${item.grossWeight}</div>`
      },
      {
        name: this.$t("handover.list.eligibilityModal.volumeWeight"),
        styleHead: "w-32 text-left whitespace-no-wrap",
        render: (item: any) =>
          `<div class="overflow-ellipsis text-left text-black-lp-300">${item.volumeWeight}</div>`
      },
      {
        name: this.$t("handover.list.eligibilityModal.reason"),
        styleHead: "w-56 text-left whitespace-no-wrap",
        render: (item: any) =>
          `<div class="text-left text-black-lp-300">${item.reason}</div>`
      }
    ];
  }
}
export default EligibilityFailedModal;
